import { alpha, Divider, Pagination, styled, Table, TablePagination } from '@mui/material';

const StyledTable = styled(Table, {
    shouldForwardProp: (prop) => prop !== 'columnDividers' && prop !== 'rowDividers' && prop !== 'rowHover' && prop !== 'dense',
})<{ columnDividers?: boolean; rowDividers?: boolean; rowHover?: boolean; dense?: boolean }>(
    ({ theme, columnDividers, rowDividers = true, rowHover, dense }) => ({
        '& .MuiTableRow-root': {
            height: !dense ? 75 : 50,
            '&.MuiTableRow-head': {
                height: 'unset',
            },
        },
        '& .MuiTableRow-root:hover td': {
            ...(rowHover && {
                backgroundColor: alpha(theme.palette.neutral.main, 0.04),
                cursor: 'pointer',
            }),
        },
        '& .MuiTableCell-root': {
            ...theme.typography.body2,
            padding: theme.spacing(1.5),
            borderBottom: rowDividers ? `1px solid ${theme.palette.dividerDark}` : 0,
            borderRight: columnDividers ? `1px solid ${theme.palette.dividerDark}` : 0,

            overflow: 'hidden',
            textOverflow: 'ellipsis',

            ':not(.checkbox-container)': {
                minWidth: 88,
                maxWidth: 312,
            },
            '& .MuiTypography-root': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },
            '&.Multiline .MuiTypography-root': {
                minWidth: !process.env.SERVER ? 200 : 'unset',
                whiteSpace: 'normal',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: '3',
                WebkitBoxOrient: 'vertical',
            },
            '&.checkbox-container': {
                cursor: 'pointer',
            },
        },
        '& .MuiTableCell-root.MuiTableCell-head': {
            ...theme.typography.subtitle5,
            color: theme.palette.text.secondary,
            whiteSpace: 'nowrap',
            textTransform: 'uppercase',
            userSelect: 'none',
            '.hover-arrow': {
                visibility: 'hidden',
            },
            ':hover .hover-arrow': {
                visibility: 'visible',
            },
        },
        ...(dense && {
            '.MuiTableCell-root .MuiTypography-root': {
                whiteSpace: 'normal',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: '2',
                WebkitBoxOrient: 'vertical',
            },
        }),
        '& tr:last-child td.MuiTableCell-root': {
            borderBottom: 0,
        },
        '& .MuiTableCell-root:last-child': {
            borderRight: 0,
        },
    }),
);

export const StyledDivider = styled(Divider)(({ theme }) => ({
    borderColor: theme.palette.dividerDark,
}));

const StyleTablePagination = styled(TablePagination)<{ rowsPerPageOptions: number[] }>(({ theme, rowsPerPageOptions }) => ({
    overflow: 'hidden',
    '& .MuiTablePagination-toolbar': {
        padding: '0 !important',
    },

    '& .MuiPagination-root': {
        flex: 1,
        display: 'flex',
        justifyContent: 'end',
    },

    '& .MuiTablePagination-selectLabel': {
        display: rowsPerPageOptions?.length > 1 ? 'block' : 'none',
        color: theme.palette.text.secondary,
        margin: 'unset',
    },

    '& .MuiTablePagination-input': {
        display: rowsPerPageOptions?.length > 1 ? 'block' : 'none',
    },

    '& .MuiTablePagination-actions': {
        display: 'flex',
        justifyContent: 'end',
    },

    '& .MuiTablePagination-displayedRows': {
        color: theme.palette.text.secondary,

        [theme.breakpoints.up('md')]:
            rowsPerPageOptions?.length > 1
                ? {
                      position: 'absolute',
                      left: '50%',
                      transform: 'translateX(-50%)',
                  }
                : undefined,
        [theme.breakpoints.down('lg')]: {
            flex: 1,
            textAlign: 'right',
        },
    },

    '& .MuiTablePagination-spacer': {
        display: 'none',
    },

    '& .MuiSelect-select': {
        color: theme.palette.primary.main,
        fontWeight: 700,
        boxSizing: 'border-box',
    },

    '& .MuiTablePagination-select.MuiSelect-select.MuiInputBase-input': {
        width: 'unset',
    },

    '& .MuiTablePagination-input .MuiSvgIcon-root': {
        color: theme.palette.primary.main,
        top: '50%',
        transform: 'translateY(-50%)',
    },
}));

StyleTablePagination.defaultProps = {
    //@ts-ignore
    component: 'span',
    size: 'small',
    labelRowsPerPage: 'View rows per page',
    labelDisplayedRows: ({ from, to, count }) => `Showing ${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`,
};

const StyledPagination = styled(Pagination)(({ theme }) => ({
    '& .MuiPaginationItem-root': {
        backgroundColor: 'unset',
        color: theme.palette.text.secondary,
        borderRadius: theme.shape.borderRadius,
        fontSize: theme.typography.body1.fontSize,
        minWidth: 28,
        height: 28,
        '& .MuiTouchRipple-child': {
            backgroundColor: theme.palette.primary.main,
        },
        '&.MuiButtonBase-root:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
        },
        '&.Mui-selected': {
            backgroundColor: 'unset',
            color: theme.palette.primary.main,
            fontWeight: 700,
            '&:hover': {
                backgroundColor: 'unset',
            },
        },
    },
    '& .MuiPaginationItem-ellipsis': {
        lineHeight: '28px',
    },
}));

StyledPagination.defaultProps = {
    size: 'small',
};

export { Box, Collapse, IconButton, Skeleton, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';

export { StyledDivider as Divider, StyledPagination as Pagination, StyledTable as Table, StyleTablePagination as TablePagination };
